import React, { useState } from 'react';
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  HStack,
  Image,
  Text,
  useColorModeValue,
  Wrap,
  WrapItem,
} from '@chakra-ui/react';
import { FaArrowRight, FaFilePdf, FaPlay } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

import { isNative, openUrl } from '../../../_Utils/mobileBridge';
import { useNovedadesAcciones } from './useNovedadesWam';

const formatearFecha = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const youtubeEmbed = (videoId) => `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;

/**
 * Una novedad: título, fecha, resumen (texto plano) y las tres salidas
 * (ir a la función, video, manual). Cada salida registra su evento sin
 * frenar la acción si el registro falla.
 */
const NovedadCard = ({ novedad, esNueva = false, onNavegar }) => {
  const navigate = useNavigate();
  const { registrarEvento } = useNovedadesAcciones();
  const [videoAbierto, setVideoAbierto] = useState(false);
  const cardBg = useColorModeValue('white', 'whiteAlpha.50');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const mutedText = useColorModeValue('gray.500', 'gray.400');
  const bodyColor = useColorModeValue('gray.700', 'gray.200');

  const evento = (tipo) => {
    registrarEvento(novedad.id, tipo).catch(() => {});
  };

  const irALaFuncion = () => {
    evento('ruta');
    onNavegar?.();
    navigate(novedad.ruta_sistema);
  };

  const verVideo = () => {
    evento('video');
    // En la app el embebido de YouTube no está verificado: se abre afuera.
    if (isNative()) {
      openUrl(novedad.video_url);
      return;
    }
    setVideoAbierto((abierto) => !abierto);
  };

  const abrirManual = () => {
    evento('manual');
    openUrl(novedad.manual_url);
  };

  return (
    <Box borderWidth="1px" borderRadius="lg" p={4} bg={cardBg} borderColor={borderColor}>
      <HStack justify="space-between" align="start" mb={1} spacing={3}>
        <Text fontWeight="bold" fontSize="md">
          {novedad.titulo}
        </Text>
        {esNueva && (
          <Badge colorScheme="green" flexShrink={0}>
            Nueva
          </Badge>
        )}
      </HStack>
      <Text fontSize="xs" color={mutedText} mb={3}>
        {formatearFecha(novedad.publicada_at)}
      </Text>
      {novedad.imagen_url && (
        <Image src={novedad.imagen_url} alt="" borderRadius="md" mb={3} maxH="220px" objectFit="cover" w="100%" />
      )}
      <Text fontSize="sm" color={bodyColor} whiteSpace="pre-line">
        {novedad.resumen}
      </Text>
      {videoAbierto && (
        <AspectRatio ratio={16 / 9} mt={3}>
          <iframe
            src={youtubeEmbed(novedad.video_id)}
            title={novedad.titulo}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </AspectRatio>
      )}
      <Wrap spacing={2} mt={3}>
        {novedad.ruta_sistema && (
          <WrapItem>
            <Button size="sm" colorScheme="blue" rightIcon={<FaArrowRight />} onClick={irALaFuncion}>
              {novedad.etiqueta_ruta || 'Ir a la función'}
            </Button>
          </WrapItem>
        )}
        {novedad.video_id && (
          <WrapItem>
            <Button size="sm" variant="outline" leftIcon={<FaPlay />} onClick={verVideo}>
              {videoAbierto ? 'Ocultar video' : 'Ver video'}
            </Button>
          </WrapItem>
        )}
        {novedad.manual_url && (
          <WrapItem>
            <Button size="sm" variant="outline" leftIcon={<FaFilePdf />} onClick={abrirManual}>
              Manual (PDF)
            </Button>
          </WrapItem>
        )}
      </Wrap>
    </Box>
  );
};

export default NovedadCard;
